import React, { useMemo } from "react";

import ArweaveLogo from "@hl/base-components/lib/assets/icons.generated/ArweaveLogo";
import BrandJustLogo from "@hl/base-components/lib/assets/icons.generated/BrandJustLogo";
import EtherscanLogo from "@hl/base-components/lib/assets/icons.generated/EtherscanLogo";
import { ViewElsewhereIcon } from "@hl/shared-features/lib/assets/icons.generated";
import {
  buildContractUrl,
  getBlockExplorerName,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import { Group, Text, UnstyledButton } from "@mantine/core";

import { getMintPageUrl } from "~config";
import useMintState from "~hooks/useMintState";

import { InternalLink } from "../InternalLink";

import { DetailSectionLine } from "./DetailSectionLine";

type Props = {
  noBottomDivider?: boolean;
  token?: {
    id?: string;
    onChainImage?: string;
  } | null;
  isApollo?: boolean;
};

export const DetailSectionViewFields = ({
  token,
  noBottomDivider = false,
  isApollo,
}: Props) => {
  const {
    collection,
    chainId,
    address,
    isAnySeriesMint,
    totalSoldCount,
    generateArweaveLink,
  } = useMintState();
  const blockchain = networkLookup(chainId);

  const browseLink = collection
    ? getMintPageUrl(collection, { marketplace: true })
    : "";

  const arweaveLink = generateArweaveLink(token?.onChainImage);

  const tokensText = useMemo(
    () => `token${totalSoldCount !== 1 ? "s" : ""}`,
    [totalSoldCount]
  );
  const mintedText = useMemo(
    () =>
      isAnySeriesMint
        ? "View collection on Highlight marketplace"
        : `${totalSoldCount || 0} ${tokensText} minted`,
    [isAnySeriesMint, totalSoldCount]
  );

  const browsePageLink = (child: JSX.Element) => {
    return isAnySeriesMint ? (
      <UnstyledButton component={InternalLink} to={browseLink}>
        {child}
      </UnstyledButton>
    ) : (
      child
    );
  };

  return (
    <>
      {collection?.id && !isApollo && (
        <DetailSectionLine
          leftContent={browsePageLink(
            <Group spacing={10} align="center" noWrap>
              <BrandJustLogo height={12} width={18} />
              <Text size="sm" sx={{ flexWrap: "nowrap", display: "flex" }}>
                {mintedText}
              </Text>
            </Group>
          )}
        />
      )}
      <DetailSectionLine
        leftContent={
          <Group spacing={10} align="center" noWrap>
            <EtherscanLogo width={18} height={18} />
            <Text size="sm">{`View contract on ${getBlockExplorerName(
              blockchain.type
            )}`}</Text>
          </Group>
        }
        rightContent={<ViewElsewhereIcon />}
        link={buildContractUrl(blockchain.type, address)}
        noDivider={noBottomDivider && !arweaveLink}
      />
      {arweaveLink && (
        <DetailSectionLine
          leftContent={
            <Group spacing={10} align="center" noWrap>
              <ArweaveLogo width={18} height={18} />
              <Text size="sm">View metadata on Arweave</Text>
            </Group>
          }
          rightContent={<ViewElsewhereIcon />}
          link={arweaveLink}
          noDivider={noBottomDivider}
        />
      )}
    </>
  );
};
